import { QualityFormulaType } from '@local/lib';
import { Badge, Button, Icon } from '@salesforce/design-system-react';
import { useState } from 'react';

import { IconCategory } from '../../../shared/types/Enum';
import {
  CONTRACT_MODE,
  CONTRACT_NUMBER,
  CONTRACT_PRICE,
  CONTRACT_PRICE_COMPONENTS,
  CONTRACT_QUANTITY,
  CONTRACT_UNIT_OF_MEASURE,
  CONTRACT_UNIT_PER_MODE,
  FORMULA_TYPES,
} from '../qualityFormulaMetadata';
import { ResultField } from './ResultField';
import { formatCurrency, formatNumber } from './utils/resultFormatting';

/**
 * Stand-in for a contract's actual price components, which this repo has no live per-contract
 * breakdown for yet (complex, index-based pricing is still design-only — see
 * docs/COMPLEX_PRICING.md). Hardcoded here purely so the section above Quality Adjustments reads
 * as a believable "previous step" rather than an empty placeholder; once real contract data
 * exists, this section should read from it instead. `formulaType` is a real `QualityFormulaType`,
 * so its label/hint come from the same `FORMULA_TYPES` metadata the Quality Adjustment "Formula
 * Type" field uses rather than an invented description. `contractNumber`/`priceComponents`/
 * `totalContractPrice`/`quantity`/`unitPerMode`/`unitOfMeasure` come from
 * `qualityFormulaMetadata.ts`'s shared contract constants so this summary, the "Price Components"
 * Price Basis's Add Component picker, and the Test Formula playground's quantity/unit/starting
 * price all read the same example contract.
 */
const CONTRACT_PRICING_EXAMPLE = {
  contractNumber: CONTRACT_NUMBER,
  formulaType: QualityFormulaType.Percentage,
  mode: CONTRACT_MODE,
  priceComponents: CONTRACT_PRICE_COMPONENTS,
  product: 'PetCoke',
  quantity: CONTRACT_QUANTITY,
  totalContractPrice: CONTRACT_PRICE,
  unitOfMeasure: CONTRACT_UNIT_OF_MEASURE,
  unitPerMode: CONTRACT_UNIT_PER_MODE,
};

const LabeledFact: React.FC<{ children: React.ReactNode; label: string }> = ({ children, label }) => (
  <span style={{ alignItems: 'baseline', display: 'inline-flex', gap: 'var(--slds-g-spacing-2)' }}>
    <span className="slds-text-body_small slds-text-color_weak">{label}</span>
    {children}
  </span>
);

/**
 * Read-only contract context shown above Quality Adjustment configuration, condensed to a single
 * compact summary line so it reads as a quick reminder rather than a second form the user has to
 * scroll past before reaching the task they came to perform (Configure Quality Specs). Everything
 * beyond the essentials (Contract #, Product, Quantity, Formula Type, Contract Price) — Mode,
 * Units per Mode, and the Price Components breakdown — is secondary detail tucked behind the
 * "Details" disclosure, collapsed by default. Entirely presentational — see
 * `CONTRACT_PRICING_EXAMPLE`'s own doc comment for why its values are hardcoded rather than
 * fetched.
 */
export const ContractPricingSummarySection: React.FC = () => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const formulaTypeOption = FORMULA_TYPES.find(option => option.value === CONTRACT_PRICING_EXAMPLE.formulaType);

  return (
    <section
      aria-label="Contract pricing summary"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-2)' }}
    >
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--slds-g-spacing-3)',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 'var(--slds-g-spacing-4)' }}>
          <span className="slds-text-title_caps slds-text-color_weak">Contract Setup</span>
          <LabeledFact label="Contract #">
            <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-6)' }}>
              {CONTRACT_PRICING_EXAMPLE.contractNumber}
            </span>
          </LabeledFact>
          <LabeledFact label="Product">
            <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-6)' }}>
              {CONTRACT_PRICING_EXAMPLE.product}
            </span>
          </LabeledFact>
          <LabeledFact label="Qty">
            <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-6)' }}>
              {formatNumber(CONTRACT_PRICING_EXAMPLE.quantity)} {CONTRACT_PRICING_EXAMPLE.unitOfMeasure}
            </span>
          </LabeledFact>
          <Badge color="default" content={formulaTypeOption?.label} />
          <span className="slds-text-body_regular" style={{ fontWeight: 'var(--slds-g-font-weight-7)' }}>
            {formatCurrency(CONTRACT_PRICING_EXAMPLE.totalContractPrice)} / {CONTRACT_PRICING_EXAMPLE.unitOfMeasure}
          </span>
          <Badge
            color="light"
            content="Read-only"
            icon={
              <Icon
                category={IconCategory.Utility}
                containerClassName="slds-m-right_xx-small"
                name="lock"
                size="xx-small"
              />
            }
          />
        </div>
        <Button
          iconCategory={IconCategory.Utility}
          iconName={detailsOpen ? 'chevronup' : 'chevrondown'}
          iconPosition="right"
          label={detailsOpen ? 'Hide Details' : 'Details'}
          onClick={() => {
            setDetailsOpen(current => !current);
          }}
          variant="base"
        />
      </div>

      {detailsOpen ? (
        <div className="slds-box" style={{ borderStyle: 'dashed' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
              <LabeledFact label="Mode">
                <Badge color="default" content={CONTRACT_PRICING_EXAMPLE.mode} />
              </LabeledFact>
              <LabeledFact label="Units per Mode">
                <Badge
                  color="default"
                  content={`${formatNumber(CONTRACT_PRICING_EXAMPLE.unitPerMode)} ${CONTRACT_PRICING_EXAMPLE.unitOfMeasure}`}
                />
              </LabeledFact>
              <LabeledFact label="Formula">
                <span className="slds-text-body_regular slds-text-color_weak">{formulaTypeOption?.hint}</span>
              </LabeledFact>
            </div>

            <hr className="slds-m-vertical_none" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="slds-text-title_caps slds-text-color_weak">Price Components</span>
              <div className="slds-grid slds-wrap slds-gutters_small">
                {CONTRACT_PRICING_EXAMPLE.priceComponents.map(component => (
                  <div className="slds-col slds-size_1-of-2 slds-medium-size_1-of-4" key={component.key}>
                    <ResultField label={component.label} value={formatCurrency(component.value * component.sign)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};
